import React, { useCallback, useEffect, useState } from "react";
import { useDebounce } from "../../../Utils/ActionUtils";
import Table, { Column, DataSourceItem } from "../../../Components/Atoms/Table";
import Tag from "../../../Components/Atoms/Tag";
import {
  InvoiceForClientPortalView,
  InvoiceStatus,
  PaymentsInvoiceAutoCollectionStatus
} from "../../../Contracts/Invoice";
import { createSortFunction } from "../../../Utils/TableUtils";
import { getPaymentMethodDetails } from "../../../Contracts/Transaction";
import { PaymentRoutes } from "../../../Contracts/Routes";
import { useInvoiceStore } from "../../../Store/Invoice";
import { ReactComponent as ExternalLinkIcon } from "../../../Assets/ActionIcons/ExternalLinkIcon.svg";
import { ReactComponent as ExclamationWarningIcon } from "../../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import ClientPortalInvoiceListPageFilters from "../../ClientPortalInvoiceListPage/ClientPortalInvoiceListPageFilters";
import { toLocalDateString } from "../../../Utils/DateUtils";
import { useTeamStore } from "../../../Store/Team";
import LoadingPage from "../../LoadingPage";
import TablePaginationFooter from "../../../Components/Atoms/Table/TablePaginationFooter";
import { useClientPortalMetadataStore } from "../../../Store/ClientPortalMetadata";
import { useParams } from "react-router-dom";
import Popover from "../../../Components/Atoms/Popover";

const ClientPortalCustomerDetailInvoiceList: React.FC = () => {
  const { customerId } = useParams();
  const [sortParamValue, setSortParamValue] = useState<string>("invoice_number:desc");
  const { partnerWhiteLabelMetadata } = useClientPortalMetadataStore();
  const { formatCurrency } = useTeamStore();
  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<string>(`collectible_invoice_customer_id:eq:${customerId}`);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(search, 1500);
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setSearch(event.target.value);
      setPage(1);
    } else {
      setSearch(undefined);
      setPage(1);
    }
  };
  const onPdfView = async (customerId: string, invoiceId: string) => {
    const pdfUrl = await getInvoicePdf(customerId, invoiceId);
    window.open(pdfUrl);
  };

  const columns: Column<InvoiceForClientPortalView>[] = [
    {
      title: "Invoice",
      dataIndex: "invoice",
      key: "invoice",
      sortClicked: createSortFunction("invoice_number", setSortParamValue),
      defaultSortOrder: "desc",
      render: d => (
        <div
          className="cursor-pointer flex items-center space-x-2"
          onClick={() => onPdfView(d.data.customer_id.toString(), d.data.id.toString())}
        >
          <div className="text-blue-600">{d.invoice}</div>
          <ExternalLinkIcon className="w-3 h-3" />
        </div>
      )
    },
    {
      title: "Invoice Status",
      dataIndex: "status",
      key: "status",
      render: d => (
        <div className="flex items-center space-x-2">
          {d.data.payment_is_processing ? (
            <Tag color="orange">Processing</Tag>
          ) : (
            <Tag
              color={
                d.data.status === InvoiceStatus.Overdue
                  ? "red"
                  : d.data.status === InvoiceStatus.Paid
                    ? "lime"
                    : d.data.status === InvoiceStatus.Open
                      ? "blue"
                      : "gray"
              }
            >
              {d.data.status}
            </Tag>
          )}
          {d.data.is_invoice_associated_with_chargeback && (
            <Popover
              message={
                <div>
                  The invoice is associated with a chargeback.{" "}
                  <a
                    href="https://help.zomentum.com/support/solutions/articles/44002440952-how-is-a-chargeback-handled-in-my-accounting-software-"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline cursor-pointer"
                  >
                    Learn More
                  </a>
                </div>
              }
            >
              <ExclamationWarningIcon className="w-3 h-3 text-red-600" />
            </Popover>
          )}
        </div>
      )
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      sortClicked: createSortFunction("total", setSortParamValue)
    },
    {
      title: "Date Issued",
      dataIndex: "dateIssued",
      key: "dateIssued",
      sortClicked: createSortFunction("issue_date", setSortParamValue),
      render: d => (
        <span title={toLocalDateString(d.data.issue_date, true)}>
          {toLocalDateString(d.data.issue_date)}
        </span>
      )
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      sortClicked: createSortFunction("due_date", setSortParamValue),
      render: d => (
        <span title={toLocalDateString(d.data.due_date, true)}>
          {toLocalDateString(d.data.due_date)}
        </span>
      )
    },
    {
      title: "Auto Collect",
      dataIndex: "autoCollect",
      key: "autoCollect",
      render: d => <Tag color={d.autoCollect === "Disabled" ? "gray" : "lime"}>{d.autoCollect}</Tag>
    },
    {
      title: "Scheduled Date",
      dataIndex: "scheduledDate",
      key: "scheduledDate",
      sortClicked: createSortFunction("auto_collection_scheduled_date", setSortParamValue),
      render: d => (
        <span title={toLocalDateString(d.data.auto_collection_scheduled_date, true)}>
          {toLocalDateString(d.data.auto_collection_scheduled_date)}
        </span>
      )
    },
    {
      title: "Payment method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: d => {
        const paymentMethodDisplay =
          d.data.payment_method && getPaymentMethodDetails(d.data.payment_method);
        return (
          <div>
            {d.data.status === InvoiceStatus.Open || d.data.status === InvoiceStatus.Overdue ? (
              <a
                href={PaymentRoutes.CHECKOUT_INVOICE_PAY.replace(
                  ":customer_id",
                  d.data.customer_id.toString()
                ).replace(":invoice_id", d.data.id.toString())}
                target="_blank"
                rel="noreferrer"
                className="flex items-center space-x-2"
              >
                <div className="text-blue-600">Pay Now</div>
                <ExternalLinkIcon className="w-3 h-3" />
              </a>
            ) : paymentMethodDisplay && d.data.payment_method_summary ? (
              <div className="capitalize flex items-center">
                <img
                  src={paymentMethodDisplay.logo}
                  alt={paymentMethodDisplay.name}
                  className="mr-2"
                />
                <span>{d.data.payment_method_summary}</span>
              </div>
            ) : (
              <div>--</div>
            )}
          </div>
        );
      }
    }
  ];
  const {
    isLoading,
    clientPortalInvoices,
    clientPortalInvoicesCount,
    getClientPortalInvoices,
    getInvoicePdf
  } = useInvoiceStore();

  const localStorageData = localStorage.getItem("CLIENT_PORTAL_CUSTOMER_DETAIL_INVOICE_LIST");
  const [limit, setLimit] = useState<10 | 25 | 50 | 100>(
    localStorageData ? JSON.parse(localStorageData).limit : 25
  );

  useEffect(() => {
    localStorage.setItem(
      "CLIENT_PORTAL_CUSTOMER_DETAIL_INVOICE_LIST",
      JSON.stringify({
        limit
      })
    );
  }, [limit]);

  const refreshThings = useCallback(async () => {
    await getClientPortalInvoices(limit, sortParamValue, page, filter, debouncedSearch);
  }, [getClientPortalInvoices, limit, sortParamValue, page, filter, debouncedSearch]);
  useEffect(() => {
    refreshThings();
  }, [refreshThings, limit, sortParamValue, page, filter, debouncedSearch]);

  const dataSource: DataSourceItem<InvoiceForClientPortalView>[] = clientPortalInvoices.map(
    invoice => ({
      key: invoice.invoice_number,
      invoice: invoice.invoice_number,
      dateIssued: toLocalDateString(invoice.issue_date),
      dueDate: toLocalDateString(invoice.due_date),
      autoCollect:
        invoice.payments_invoice_auto_collection_status ===
        PaymentsInvoiceAutoCollectionStatus.Pending
          ? "Enabled"
          : "Disabled",
      total: formatCurrency(invoice.total, partnerWhiteLabelMetadata?.currency),
      data: invoice,
      scheduledDate: toLocalDateString(invoice.auto_collection_scheduled_date)
    })
  );

  return (
    <div>
      <div className="flex flex-col h-[calc(100vh-11rem)]">
        <div className="flex justify-between mt-2 mb-1">
          <ClientPortalInvoiceListPageFilters
            setFilter={setFilter}
            defaultFilter={`collectible_invoice_customer_id:eq:${customerId}`}
          />
          <div>
            <input
              className="px-2 py-1 border rounded"
              placeholder="Search here"
              value={search || ""}
              onChange={handleSearchInputChange}
            />
          </div>
        </div>
        <div className="flex flex-grow flex-col overflow-y-auto">
          {isLoading && clientPortalInvoices.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
          )}
        </div>
        <TablePaginationFooter
          limit={limit}
          setLimit={setLimit}
          setPage={setPage}
          totalRecords={clientPortalInvoicesCount}
          page={page}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
export default ClientPortalCustomerDetailInvoiceList;
